/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Modal, ModalBody, ModalHeader, TabContent, TabPane } from 'reactstrap';
import SDGSelector from '../../components/update-materiality-modal/SDGSelector';
import { InitiativeData } from '../../types/initiative';
import CustomMateriality from '../../components/add-utr-modal/CustomMateriality';
import CustomTarget from './CustomTarget';
import { BasicAlert } from '../alert/BasicAlert';
import { ViewSelector } from './ViewSelector';
import { Menu } from '../menu/Menu';

interface TargetActualProps {
  initiative?: InitiativeData;
  isOpen: boolean;
  toggle: () => void;
  message: string;
  saving: boolean;
  handleUpdate: (value: string[]) => void;
  handleCancel: () => void;
  handleClearMessage: () => void;
  handleReload: () => Promise<void>;
  handleReloadScoreCard: () => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
}

export interface TargetValuesProps {
  suggestions: SuggestionsProps[]
  loading: boolean;
  goalCode: string | undefined;
  errorMessage: string;
}

export interface SuggestionsProps {
  code: string;
  name: string;
  _id: string;
}

export const TABS = {
  IndustryMateriality: 'industry-materiality',
  CustomMateriality: 'custom-materiality',
  CustomTargets: 'custom-target',
  Settings: 'settings',
}

const TargetActualModal = (props: TargetActualProps) => {
  const {
    isOpen,
    toggle,
    handleReload,
    handleReloadScoreCard,
    initiative,
    handleUpdate,
    message,
    saving,
    handleClearMessage,
    activeTab,
    setActiveTab
  } = props;

  const [targetValues, setTargetValues] = useState<TargetValuesProps>({
    suggestions: [],
    loading: false,
    goalCode: undefined,
    errorMessage: ''
  });

  if (!initiative) {
    return <></>;
  }
  const toggleOpen = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  }

  return <Modal isOpen={isOpen} toggle={toggle} backdrop='static' className='targetsValuesModal'>
    <ModalHeader toggle={toggle} >Targets and values</ModalHeader>
    <ModalBody>
      <BasicAlert type={'danger'}>{targetValues.errorMessage}</BasicAlert>
      <Menu
        grouped
        items={[
          {
            active: activeTab === TABS.IndustryMateriality,
            onClick: () => toggleOpen(TABS.IndustryMateriality),
            label: 'SDG materiality'
          },
          {
            active: activeTab === TABS.CustomMateriality,
            onClick: () => toggleOpen(TABS.CustomMateriality),
            label: 'SDG targets'
          },
          {
            active: activeTab === TABS.CustomTargets,
            onClick: () => toggleOpen(TABS.CustomTargets),
            label: 'Custom targets'
          },
          {
            active: activeTab === TABS.Settings,
            onClick: () => toggleOpen(TABS.Settings),
            label: 'Settings'
          }
        ]
        }
      />
      <TabContent activeTab={activeTab} className='p-3'>
        <TabPane tabId={TABS.IndustryMateriality}>
          <SDGSelector
            handleReload={handleReloadScoreCard}
            toggle={toggle}
            initiative={initiative}
          />
        </TabPane>
        <TabPane tabId={TABS.CustomMateriality}>
          <CustomMateriality
            message={message}
            saving={saving}
            toggle={toggle}
            handleUpdate={handleUpdate}
            handleClearMessage={handleClearMessage}
            targetValues={targetValues}
            setTargetValues={setTargetValues}
          />
        </TabPane>
        <TabPane tabId={TABS.CustomTargets}>
          <CustomTarget
            key={initiative._id}
            onCancel={toggle}
            initiativeId={initiative._id}
            handleReload={handleReload} />
        </TabPane>
        <TabPane tabId={TABS.Settings}>
          <ViewSelector
            onCancel={toggle}
            initiative={initiative}
            handleReload={handleReload} />
        </TabPane>
      </TabContent>
    </ModalBody>
  </Modal>
}

export default TargetActualModal;


